import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import './editor.css';

/*
 * A barra de escrever. Modelo C (ações dentro do campo) do lote 4 do design
 * system: modo, anexo, microfone, campo e enviar numa caixa só.
 *
 * É a peça mais usada do produto, e cada faixa de cromo que ela ocupa é uma
 * mensagem a menos visível na conversa acima. O que estava no ar tinha quatro
 * faixas empilhadas — aviso da janela, linha de modo, campo e linha de ações.
 *
 * ── O que difere da entrega, e por quê ──────────────────────────────────
 *
 * O componente é a CASCA: quem sabe de anexos, gravação, mensagens prontas e
 * limites do canal é a tela, que entra por `children` (acima da caixa) e
 * pelos callbacks. Para isso a entrega ganhou cinco coisas:
 *
 *   - as ações de ícone aceitam `{ aoClicar, motivo, rotulo, pressionado,
 *     expandido }`: um ícone desabilitado precisa dizer por quê, e o
 *     microfone gravando precisa mudar de rótulo e de estado;
 *   - `motivoParaNaoEnviar`: a tela tem razões que a casca não conhece —
 *     passou do limite do canal, anexo recusado;
 *   - `invalido`: `aria-invalid` no campo quando o texto passou do limite;
 *   - `aoSoltarArquivos`: arrastar arquivo por cima da caixa;
 *   - `campoRef`: a tela precisa do elemento para inserir emoji no cursor;
 *   - a janela fechada aceita `texto` e `rotuloDaAcao` próprios, porque o
 *     produto chama os modelos de "mensagens prontas" e o motivo vem do canal.
 *
 * O valor é controlado pela tela. Foi decisão, não descuido: o rascunho
 * precisa sobreviver a trocar de modo, a receber uma mensagem nova e a trocar
 * de conversa e voltar. Estado interno não sobrevive a nada disso.
 */

export type ModoEditor = 'canal' | 'nota';

/**
 * O estado da janela de 24 h do WhatsApp.
 *
 * `aberta` não aparece na interface de propósito: ver "restam 23 h 40 min" o
 * dia inteiro ensina a pessoa a não ler aquela faixa.
 */
export type Janela =
  | { estado: 'aberta' }
  | { estado: 'acabando'; emPalavras: string }
  | { estado: 'fechada'; texto?: string; aoUsarModelo?: () => void; rotuloDaAcao?: string };

/** Uma ação de ícone. Desabilitada exige `motivo`, como no botão. */
export type AcaoDoEditor = {
  aoClicar: () => void;
  rotulo?: string;
  motivo?: string;
  pressionado?: boolean;
  expandido?: boolean;
};

export interface ItemSugerido {
  readonly id: string;
  readonly titulo: string;
  readonly texto: string;
}

/**
 * A lista suspensa do campo — hoje, as mensagens prontas chamadas por `/`.
 *
 * O componente não sabe o que são os itens nem de onde vêm: ele desenha,
 * navega pelo teclado e devolve a escolha. Quem monta a lista é a tela.
 */
export interface SugestoesDoEditor {
  readonly aberta: boolean;
  readonly itens: readonly ItemSugerido[];
  /** O índice destacado — é ele que o Enter escolhe. */
  readonly ativo: number;
  /** A frase de quando não há o que listar. Sem ela, a caixa não abre vazia. */
  readonly vazio?: string;
  readonly aoMover: (delta: number) => void;
  readonly aoEscolher: (indice: number) => void;
  readonly aoFechar: () => void;
}

export type EditorProps = {
  valor: string;
  aoMudar: (valor: string) => void;
  aoEnviar: () => void;
  modo: ModoEditor;
  aoTrocarModo: (modo: ModoEditor) => void;
  /** Nome do canal no seletor: "WhatsApp", "Instagram". */
  nomeDoCanal?: string;
  janela?: Janela;
  aoAnexar?: AcaoDoEditor | (() => void);
  aoGravar?: AcaoDoEditor | (() => void);
  aoEmoji?: AcaoDoEditor | (() => void);
  /**
   * Abrir as mensagens prontas — as aprovadas pela Meta.
   *
   * ── Por que virou ícone, e não só a saída da janela fechada ────────────
   *
   * Ele existia só dentro do aviso de "janela de 24 h fechada", como a única
   * saída de quem não podia mais escrever livremente. Isso o transformava em
   * um remédio: aparecia quando já era tarde, e sumia quando a conversa estava
   * viva.
   *
   * Mensagem aprovada não é remédio. Ela é o jeito certo de mandar "seu carro
   * está pronto para retirada" — com o texto que a Meta leu e liberou — e quem
   * atende quer mandá-la NO MEIO da conversa, não só depois que ela esfriou.
   *
   * ── E por que ele NÃO obedece a `travado` ──────────────────────────────
   *
   * Os outros ícones morrem com a janela fechada, porque o que eles produzem
   * (texto livre, áudio, foto) é justamente o que não pode sair. Este é o
   * contrário: fora da janela ele é a ÚNICA coisa que pode sair, e desabilitá-lo
   * ali deixaria a pessoa sem nenhuma saída.
   */
  aoMensagemPronta?: AcaoDoEditor | (() => void);
  /**
   * Compartilhar um contato cadastrado (#595). Diferente das mensagens
   * prontas, obedece a `travado`: o que ele produz é texto livre, e texto
   * livre é o que não sai com a janela fechada.
   */
  aoCompartilharContato?: AcaoDoEditor | (() => void);
  /** Envio em curso: trava o botão sem apagar o texto. */
  enviando?: boolean;
  /** A tela sabe de razões que a casca não conhece; vai no `title` do botão. */
  motivoParaNaoEnviar?: string;
  /** O texto passou do limite do canal. */
  invalido?: boolean;
  /**
   * Há anexo pronto para sair. A casca só conhece o texto; sem isto, uma foto
   * anexada com o campo vazio deixava o Enviar travado — e foto sem legenda é
   * o envio mais comum de um vendedor.
   */
  temAnexo?: boolean;
  /**
   * Há MENSAGEM PRONTA escolhida — e ela dispensa o texto.
   *
   * Irmã de `temAnexo`, e pelo mesmo motivo: a casca só conhece o campo. Com
   * modelo escolhido, o texto que sai é o que a Meta aprovou, e o campo fica
   * DESABILITADO — digitar ali não muda o que o cliente recebe.
   *
   * Sem isto o Enviar ficava travado com o modelo à vista, e a única saída era
   * escrever algo que nunca seria entregue. Medido em 14/09/2026, com quem
   * estava atendendo parado na tela.
   */
  temMensagemPronta?: boolean;
  /** Arrastar arquivo por cima da caixa. Sem ele, a caixa não reage. */
  aoSoltarArquivos?: (arquivos: File[]) => void;
  /** O elemento do campo, para quem precisa do cursor. */
  campoRef?: RefObject<HTMLTextAreaElement | null>;
  /**
   * A lista que aparece ACIMA do campo enquanto se digita um comando (H05c).
   *
   * Mora aqui, e não na tela do atendimento, por um motivo só: o teclado. As
   * setas e o Enter precisam ser interceptados no `onKeyDown` do campo, e o
   * campo é deste componente. Quem decide O QUE entra na lista continua sendo
   * quem a usa — daqui sai apenas o desenho e a navegação.
   */
  sugestoes?: SugestoesDoEditor;
  /** Conteúdo extra acima da caixa: prévia de anexo, gravação, avisos. */
  children?: ReactNode;
  /**
   * O seletor de emoji, para abrir COLADO ao botão de emoji (v3): quem o
   * monta é a tela, mas o lugar dele é ao lado do ícone, dentro das
   * ferramentas — longe dali ele abria no canto da caixa.
   */
  seletorDeEmoji?: ReactNode;
};

function Glifo({
  nome,
}: {
  nome:
    'anexo' | 'microfone' | 'emoji' | 'enviar' | 'mensagem-pronta' | 'contato' | 'canal' | 'nota';
}): ReactNode {
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {nome === 'anexo' ? (
        <path d="M14.4 9.1 9.6 13.9a2.7 2.7 0 0 1-3.8-3.8l5.4-5.4a1.8 1.8 0 1 1 2.5 2.5l-5.3 5.4a.9.9 0 0 1-1.3-1.3l4.7-4.7" />
      ) : null}
      {nome === 'microfone' ? (
        <>
          <rect x="7.6" y="2.8" width="4.8" height="8.4" rx="2.4" />
          <path d="M4.8 9.2a5.2 5.2 0 0 0 10.4 0" />
          <path d="M10 14.4v2.8" />
        </>
      ) : null}
      {nome === 'emoji' ? (
        <>
          <circle cx="10" cy="10" r="6.8" />
          <path d="M7.4 8.2h.01M12.6 8.2h.01" />
          <path d="M7.3 11.8a3.4 3.4 0 0 0 5.4 0" />
        </>
      ) : null}
      {/* Os dois modos, para quando o editor estreita e o seletor vira ícone:
          o balão vazio é a conversa com o cliente; a folha de canto dobrado,
          o bilhete que fica na equipe. */}
      {nome === 'canal' ? (
        <path d="M16.6 12.6a1.6 1.6 0 0 1-1.6 1.6H7.4L4 17V4.8a1.6 1.6 0 0 1 1.6-1.6H15a1.6 1.6 0 0 1 1.6 1.6Z" />
      ) : null}
      {nome === 'nota' ? (
        <>
          <path d="M12.4 3.2H5.2a1.6 1.6 0 0 0-1.6 1.6v10.4a1.6 1.6 0 0 0 1.6 1.6h9.6a1.6 1.6 0 0 0 1.6-1.6V7.2Z" />
          <path d="M12 3.2v4.4h4.4" />
        </>
      ) : null}
      {nome === 'enviar' ? <path d="M3.4 10 16.6 3.6 13 16.4l-3.6-4.3-6-2.1Z" /> : null}
      {/* Um cartão com uma pessoa dentro: é contato, e é para entregar. */}
      {nome === 'contato' ? (
        <>
          <rect x="2.8" y="4.2" width="14.4" height="11.6" rx="2" />
          <circle cx="7.4" cy="8.9" r="1.7" />
          <path d="M4.9 13.2a2.7 2.7 0 0 1 5 0M11.8 8.4h3M11.8 11.2h3" />
        </>
      ) : null}
      {/*
        Balão de fala com duas linhas dentro: é mensagem, e é mensagem que já
        vem escrita. O balão sozinho seria "conversa" — que é a tela inteira.
      */}
      {nome === 'mensagem-pronta' ? (
        <>
          <path d="M16.8 12.4a1.6 1.6 0 0 1-1.6 1.6H7.2L4 17.2V4.4a1.6 1.6 0 0 1 1.6-1.6h9.6a1.6 1.6 0 0 1 1.6 1.6Z" />
          <path d="M7 7.2h6.4M7 10h4" />
        </>
      ) : null}
    </svg>
  );
}

const acao = (a: AcaoDoEditor | (() => void) | undefined): AcaoDoEditor | undefined =>
  typeof a === 'function' ? { aoClicar: a } : a;

function Icone({
  nome,
  rotuloPadrao,
  acao: a,
  travado,
}: {
  nome: 'anexo' | 'microfone' | 'emoji' | 'mensagem-pronta' | 'contato';
  rotuloPadrao: string;
  acao: AcaoDoEditor;
  travado?: string;
}): ReactNode {
  const motivo = a.motivo ?? travado;
  return (
    <button
      type="button"
      className={`editor__icone editor__icone--${nome}`}
      onClick={a.aoClicar}
      disabled={motivo !== undefined}
      aria-label={a.rotulo ?? rotuloPadrao}
      title={motivo ?? a.rotulo ?? rotuloPadrao}
      aria-pressed={a.pressionado}
      aria-expanded={a.expandido}
    >
      <Glifo nome={nome} />
    </button>
  );
}

export function Editor({
  valor,
  aoMudar,
  aoEnviar,
  modo,
  aoTrocarModo,
  nomeDoCanal = 'WhatsApp',
  janela = { estado: 'aberta' },
  aoAnexar,
  aoGravar,
  aoEmoji,
  aoMensagemPronta,
  aoCompartilharContato,
  enviando = false,
  motivoParaNaoEnviar,
  invalido = false,
  temAnexo = false,
  temMensagemPronta = false,
  aoSoltarArquivos,
  campoRef,
  sugestoes,
  children,
  seletorDeEmoji,
}: EditorProps): ReactNode {
  const campo = useRef<HTMLTextAreaElement | null>(null);
  const raiz = useRef<HTMLDivElement | null>(null);
  const [arrastando, definirArrastando] = useState(false);
  /*
   * Aberta: o texto passou de uma linha, e a faixa única vira duas — modo e
   * ferramentas em cima, o campo com a largura inteira embaixo (só no desktop;
   * o celular já é de duas linhas). Na faixa única o campo fica com o que
   * sobra entre o modo, as ferramentas e o enviar, e uma resposta longa virava
   * uma coluna estreita de texto encostada no enviar.
   *
   * Liga quando o texto quebra e só desliga com o campo vazio — depois de
   * enviar. Desligar ao voltar a caber numa linha faria a caixa pular entre os
   * dois desenhos enquanto se digita, porque na faixa larga o mesmo texto cabe.
   */
  const [aberta, definirAberta] = useState(false);
  const nota = modo === 'nota';
  const fechada = janela.estado === 'fechada';
  const vazio = valor.trim().length === 0 && !temAnexo && !temMensagemPronta;

  /*
   * O campo acompanha o conteúdo.
   *
   * `height: auto` antes de ler `scrollHeight`: sem isso a caixa só cresce e
   * nunca encolhe ao apagar. Roda a cada mudança de `valor`, e não só no
   * `input`, para acertar também quando o texto vem de fora — um modelo de
   * mensagem escolhido, um rascunho restaurado.
   */
  useEffect(() => {
    const elemento = campo.current;
    if (!elemento) return;
    const ajustar = (): void => {
      elemento.style.height = 'auto';
      // Com `height: auto` e `rows={1}`, a altura visível é a de uma linha.
      const umaLinha = elemento.clientHeight;
      if (valor.length === 0) definirAberta(false);
      else if (elemento.scrollHeight > umaLinha + 1) definirAberta(true);
      // `scrollHeight` não conta a borda; sem somá-la, o campo com contorno
      // (a faixa estreita) ficaria 2px curto e ganharia barra de rolagem.
      const borda = elemento.offsetHeight - elemento.clientHeight;
      elemento.style.height = `${elemento.scrollHeight + borda}px`;
    };
    ajustar();
    /* A coluna da conversa muda de largura sem o texto mudar — abrir ou fechar
       o painel do cliente, redimensionar a janela —, e a altura que servia
       antes passa a cortar linhas ou a sobrar. */
    const raizAtual = raiz.current;
    if (!raizAtual || typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver(ajustar);
    observador.observe(raizAtual);
    return () => observador.disconnect();
  }, [valor, aberta]);

  /* Um id por editor: a tela tem dois (resposta e nota interna), e um id fixo
     faria o `aria-controls` de um apontar para a lista do outro. */
  const idDasSugestoes = useId();

  const noTeclado = (evento: KeyboardEvent<HTMLTextAreaElement>): void => {
    /*
     * Com a lista aberta, o teclado é DELA — e isto vem antes de tudo.
     *
     * O Enter é o caso que importa: sem esta guarda ele enviaria `/ajuda` ao
     * cliente, que é exatamente o contrário do que a pessoa pediu ao digitar o
     * comando. As setas seriam movimento do cursor dentro do texto, e o Esc
     * não faria nada.
     */
    if (sugestoes?.aberta) {
      if (evento.key === 'ArrowDown') {
        evento.preventDefault();
        sugestoes.aoMover(1);
        return;
      }
      if (evento.key === 'ArrowUp') {
        evento.preventDefault();
        sugestoes.aoMover(-1);
        return;
      }
      if (evento.key === 'Escape') {
        evento.preventDefault();
        sugestoes.aoFechar();
        return;
      }
      if (evento.key === 'Enter' && !evento.shiftKey) {
        evento.preventDefault();
        if (sugestoes.itens.length > 0) sugestoes.aoEscolher(sugestoes.ativo);
        return;
      }
    }

    // Enter envia, Shift+Enter quebra linha. É o gesto que a pessoa já traz do
    // telefone, e mudá-lo custaria mais que qualquer ganho de consistência.
    if (evento.key === 'Enter' && !evento.shiftKey) {
      evento.preventDefault();
      if (!vazio && !fechada && !enviando && !motivoParaNaoEnviar) aoEnviar();
    }
  };

  const motivo =
    motivoParaNaoEnviar ??
    (fechada ? 'A janela de 24 h fechou' : vazio ? 'Escreva algo para enviar' : undefined);
  const travado = fechada ? (janela.texto ?? 'A janela de 24 h fechou') : undefined;

  const anexar = acao(aoAnexar);
  const gravar = acao(aoGravar);
  const emoji = acao(aoEmoji);
  const mensagemPronta = acao(aoMensagemPronta);
  const compartilharContato = acao(aoCompartilharContato);

  return (
    <div
      ref={raiz}
      className={[
        'editor',
        aberta ? 'editor--aberta' : null,
        nota ? 'editor--nota' : null,
        fechada ? 'editor--fechada' : null,
        arrastando ? 'editor--arrastando' : null,
      ]
        .filter(Boolean)
        .join(' ')}
      onDragOver={
        aoSoltarArquivos
          ? (evento) => {
              evento.preventDefault();
              definirArrastando(true);
            }
          : undefined
      }
      onDragLeave={aoSoltarArquivos ? () => definirArrastando(false) : undefined}
      onDrop={
        aoSoltarArquivos
          ? (evento) => {
              evento.preventDefault();
              definirArrastando(false);
              aoSoltarArquivos([...evento.dataTransfer.files]);
            }
          : undefined
      }
    >
      {janela.estado === 'acabando' ? (
        <div className="editor__aviso editor__aviso--acabando" role="status">
          <span aria-hidden="true">⏱</span>
          <span>
            Faltam <b>{janela.emPalavras}</b> para a janela de 24 h fechar.
          </span>
        </div>
      ) : null}

      {janela.estado === 'fechada' ? (
        <div className="editor__aviso editor__aviso--fechada" role="status">
          <span aria-hidden="true">⚠</span>
          <span>
            {janela.texto ?? 'A janela de 24 h fechou. Só dá para enviar um modelo aprovado.'}
          </span>
          {janela.aoUsarModelo ? (
            <button className="editor__aviso-acao" type="button" onClick={janela.aoUsarModelo}>
              {janela.rotuloDaAcao ?? 'Escolher modelo'}
            </button>
          ) : null}
        </div>
      ) : null}

      {children}

      {/*
        A lista fica ACIMA do campo, e FORA da caixa.

        Abaixo ela cobriria o Enviar e, no celular, nasceria atrás do teclado do
        sistema — invisível justamente onde o espaço é menor.

        Fora da caixa porque a caixa é uma FAIXA em `flex`: modo, anexo,
        microfone, campo e enviar dividem uma linha só. Dentro dela a lista
        virava mais um item da faixa e se espremia entre as abas e o campo,
        quebrando a linha inteira — foi o que apareceu em uso. Aqui ela é irmã
        da caixa, e o CSS a sobrepõe sem empurrar a conversa para cima.
      */}
      {sugestoes?.aberta && (
        <div className="editor__sugestoes">
          {sugestoes.itens.length === 0 ? (
            <p className="editor__sugestoes-vazio" role="status">
              {sugestoes.vazio}
            </p>
          ) : (
            <ul className="editor__sugestoes-lista" role="listbox" id={idDasSugestoes}>
              {sugestoes.itens.map((item, indice) => (
                /*
                    A opção NÃO é um botão, e isto é o padrão de combobox.

                    Quem tem o foco é o campo — é ele que recebe as setas e o
                    Enter, e o `aria-activedescendant` aponta qual opção está
                    ativa. Um botão aqui seria controle focável dentro de
                    `role="option"`: o axe recusa, e com razão, porque a
                    tabulação passaria a parar em cada item de uma lista que se
                    navega com seta.

                    `onMouseDown` e não `onClick`: o clique tira o foco do campo
                    antes de disparar, e o campo perdido fecharia a lista antes
                    de a escolha acontecer.
                  */
                <li
                  key={item.id}
                  id={`${idDasSugestoes}-${indice}`}
                  role="option"
                  aria-selected={indice === sugestoes.ativo}
                  className="editor__sugestao"
                  data-ativo={indice === sugestoes.ativo ? 'sim' : undefined}
                  onMouseDown={(evento) => {
                    evento.preventDefault();
                    sugestoes.aoEscolher(indice);
                  }}
                >
                  <span className="editor__sugestao-titulo">{item.titulo}</span>
                  <span className="editor__sugestao-texto">{item.texto}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="editor__caixa">
        <div className="editor__modo" role="tablist" aria-label="Como responder">
          <button
            type="button"
            role="tab"
            className="editor__modo-op"
            aria-selected={!nota}
            onClick={() => aoTrocarModo('canal')}
            title={nomeDoCanal}
          >
            {/* O glifo só aparece com o editor estreito; o rótulo, aí, sai da
                tela mas fica no DOM — é ele o nome acessível do botão. */}
            <span className="editor__modo-glifo" aria-hidden="true">
              <Glifo nome="canal" />
            </span>
            <span className="editor__modo-rotulo">{nomeDoCanal}</span>
          </button>
          <button
            type="button"
            role="tab"
            className="editor__modo-op"
            aria-selected={nota}
            onClick={() => aoTrocarModo('nota')}
            title="Nota interna"
          >
            <span className="editor__modo-glifo" aria-hidden="true">
              <Glifo nome="nota" />
            </span>
            <span className="editor__modo-rotulo">Nota interna</span>
          </button>
        </div>

        {/*
          Duas caixas a mais no DOM, invisíveis no desktop (`display: contents`)
          e reais no celular: a linha, que separa o campo do modo, e a pílula,
          que abraça microfone, campo e clipe e deixa o enviar de fora. Sem
          elas o celular dependia de `flex-wrap`, e o campo — que tem largura
          própria — empurrava o enviar para uma terceira linha.
        */}
        <div className="editor__linha">
          <div className="editor__pilula">
            {gravar ? (
              <Icone nome="microfone" rotuloPadrao="Gravar áudio" acao={gravar} travado={travado} />
            ) : null}
            {/*
              As ferramentas à esquerda do campo, como no desenho (v3). No
              celular o grupo se desfaz (`display: contents`) e os ícones
              voltam a se distribuir pela pílula.
            */}
            <div className="editor__ferramentas">
              {anexar ? (
                <Icone nome="anexo" rotuloPadrao="Anexar arquivo" acao={anexar} travado={travado} />
              ) : null}
              {emoji ? (
                <span className="editor__ancora-emoji">
                  <Icone nome="emoji" rotuloPadrao="Inserir emoji" acao={emoji} travado={travado} />
                  {seletorDeEmoji}
                </span>
              ) : null}
              {/*
              Duas notas sobre este ícone.

              Ele NÃO recebe `travado` — ver `aoMensagemPronta`. Com a janela
              fechada, é o único ícone que ainda leva a algum lugar.

              E ele se chama "Mensagens prontas", não "Escolher mensagem
              pronta": o aviso de janela fechada já tem um botão com esse nome,
              e aquele é uma INSTRUÇÃO — a janela fechou, escolha uma. Este é um
              LUGAR, a lista que está sempre ali. Dois botões com o mesmo nome
              na mesma região fazem o leitor de tela anunciar a mesma coisa duas
              vezes sem dizer que são caminhos diferentes.
            */}
              {mensagemPronta ? (
                <Icone
                  nome="mensagem-pronta"
                  rotuloPadrao="Mensagens prontas"
                  acao={mensagemPronta}
                />
              ) : null}
              {compartilharContato ? (
                <Icone
                  nome="contato"
                  rotuloPadrao="Compartilhar contato"
                  acao={compartilharContato}
                  travado={travado}
                />
              ) : null}
            </div>
            <textarea
              ref={(elemento) => {
                campo.current = elemento;
                if (campoRef) campoRef.current = elemento;
              }}
              className="editor__campo"
              rows={1}
              value={valor}
              onChange={(evento) => aoMudar(evento.target.value)}
              onKeyDown={noTeclado}
              {...(sugestoes
                ? {
                    role: 'combobox',
                    'aria-expanded': sugestoes.aberta,
                    'aria-controls': idDasSugestoes,
                    'aria-autocomplete': 'list' as const,
                    ...(sugestoes.aberta && sugestoes.itens.length > 0
                      ? { 'aria-activedescendant': `${idDasSugestoes}-${sugestoes.ativo}` }
                      : {}),
                  }
                : {})}
              /*
               * Com mensagem pronta, o campo TRAVA.
               *
               * O que sai é o texto aprovado pela Meta; o que se digitasse
               * aqui nunca chegaria ao cliente e ficaria só no histórico,
               * como se tivesse sido enviado. Deixar escrever é convidar ao
               * engano — e foi o que aconteceu.
               */
              disabled={fechada || temMensagemPronta}
              aria-invalid={invalido ? true : undefined}
              aria-label={nota ? 'Anotação para a equipe' : `Resposta pelo ${nomeDoCanal}`}
              placeholder={
                temMensagemPronta
                  ? 'A mensagem pronta acima é o que será enviado.'
                  : nota
                    ? 'Anotação para a equipe. O cliente não vê.'
                    : 'Digite sua mensagem…'
              }
            />
            {/* A dica do atalho, à direita do campo (v3): só no mouse. */}
            <span className="editor__dica" aria-hidden="true">
              <kbd>Shift + Enter</kbd> para nova linha
            </span>
          </div>

          <button
            type="button"
            className="editor__enviar"
            onClick={aoEnviar}
            disabled={vazio || fechada || enviando || motivoParaNaoEnviar !== undefined}
            title={motivo}
            // O botão é só o glifo em toda largura (v3), e o rótulo visível
            // fica fora do fluxo: o nome acessível vem daqui.
            aria-label={nota ? 'Salvar nota' : 'Enviar'}
          >
            <span className="editor__enviar-rotulo">{nota ? 'Salvar nota' : 'Enviar'}</span>
            <span className="editor__enviar-glifo" aria-hidden="true">
              <Glifo nome="enviar" />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
